import { useCallback, useState } from 'react';

import { checkBirthDate } from '@psy/validations';

import { useLanguage } from '../../contexts/language';
import { useField, validateAll } from '../../hooks/field';
import { useUpdateProfile } from '../../hooks/update-profile';
import { useVisit } from '../../hooks/visit';

import { PageTitle } from '../page-title';
import { StepActions } from '../step-actions';
import { TextField } from '../text-field';

import styles from './style.module.css';

// Step 2: the birth date, as a native date input. The device shows its own
// picker and its own day/month order, which is the language rule (C18)
// applied to dates. max stops the picker at today, and the check refuses a
// date typed past it.
export function BirthDateStep({ profile, onBack, onDone }) {
  const { text, translate } = useLanguage();
  const updateProfile = useUpdateProfile();
  const capture = useVisit();
  const [ busy, setBusy ] = useState(false);

  // Today in the device's own zone, which is the day the person means. Read
  // once: a form left open over midnight keeps the day it was opened on.
  const [ today ] = useState(function() {
    const now = new Date();

    return `${ now.getFullYear() }-${ String(now.getMonth() + 1).padStart(2, '0') }-${ String(now.getDate()).padStart(2, '0') }`;
  });

  const check = useCallback(function(value) {
    return checkBirthDate({ value, today });
  }, [ today ]);

  const birthDate = useField({ initial: profile.birthDate, check });

  const onSubmit = useCallback(function(event) {
    event.preventDefault();

    if (validateAll([ birthDate ]) === false) {
      return null;
    }

    setBusy(true);

    const owns = capture();

    return updateProfile({ birthDate: birthDate.value }).then(function() {
      if (owns() === false) {
        return null;
      }

      return onDone();
    }).catch(function(failed) {
      if (owns() === false) {
        return;
      }

      setBusy(false);
      birthDate.refuse(failed.message);
    });
  }, [ birthDate, updateProfile, onDone, capture ]);

  return (
    <form className={ styles.form } onSubmit={ onSubmit } noValidate>
      <PageTitle title={ text.birthTitle }/>

      <TextField
        ref={ birthDate.ref }
        id='birth-date'
        label={ text.birthDate }
        type='date'
        value={ birthDate.value }
        onChange={ birthDate.onChange }
        onBlur={ birthDate.onBlur }
        error={ translate(birthDate.error) }
        hint={ text.birthHint }
        autoComplete='bday'
        min='1900-01-01'
        max={ today }
      />

      <StepActions onBack={ onBack } submitLabel={ text.continue } busy={ busy }/>
    </form>
  );
}
